import { config } from "@/app/config";
import { router } from "@/app/router";
import { store } from "@/app/store";
import { theme } from "@/app/theme";
import { AuthTokenBridge } from "@/shared/auth/AuthTokenBridge";
import i18n from "@/shared/i18n/i18n";
import { type AppState, Auth0Provider } from "@auth0/auth0-react";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import type { ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import { Provider as ReduxProvider } from "react-redux";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "mantine-datatable/styles.css";

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.auth0Audience,
        scope: "openid profile email offline_access",
      }}
      cacheLocation="memory"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      onRedirectCallback={(appState?: AppState) => {
        void router.navigate(appState?.returnTo ?? "/", { replace: true });
      }}
    >
      <AuthTokenBridge />
      <ReduxProvider store={store}>
        <I18nextProvider i18n={i18n}>
          <MantineProvider theme={theme} defaultColorScheme="auto">
            <Notifications position="top-right" />
            <ModalsProvider>{children}</ModalsProvider>
          </MantineProvider>
        </I18nextProvider>
      </ReduxProvider>
    </Auth0Provider>
  );
}
